import type { SoapFault } from '@zimbra-webif/types';

/**
 * A Zimbra SOAP Fault surfaced as a typed error.
 *
 * The browser/UI catches these to distinguish "auth expired → re-login" from
 * genuine failures, without parsing raw fault bodies.
 */
export class ZimbraError extends Error {
  readonly code: string;
  readonly trace?: string;
  readonly fault: SoapFault;

  constructor(fault: SoapFault) {
    const code = fault.Detail?.Error.Code ?? fault.Code?.Value ?? 'service.FAILURE';
    super(fault.Reason?.Text ?? code);
    this.name = 'ZimbraError';
    this.code = code;
    this.trace = fault.Detail?.Error.Trace;
    this.fault = fault;
  }

  /**
   * The auth token is missing/expired/invalid → the caller should re-authenticate.
   *
   * NOTE: exact code strings are Zimbra `ServiceException` identifiers; confirm
   * the full set against a live capture before relying on it broadly.
   */
  get isAuthExpired(): boolean {
    return this.code === 'service.AUTH_EXPIRED' || this.code === 'service.AUTH_REQUIRED';
  }
}

/** A transport-level failure (network, non-SOAP HTTP error, malformed body). */
export class TransportError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'TransportError';
    this.status = status;
  }
}
